import { Injectable, signal } from '@angular/core';
import { RollMonthlyCostDto } from '../../../proxies/monthly-cost.proxy.service';
import { ProcessSummaryReportDto } from '../../../proxies/cost-reports.proxy.service';
import { ColorRecord, CostSourceFilter, OrderType, WorkOrderSummary } from '../models/cost-analysis.models';

export type CostAnalysisTabKey = 'by-process' | 'by-fabric' | 'by-component';

export interface CostAnalysisFilters {
  fabricCodes: string[];
  colorReferences: string[];
  productionCardNumbers: string[];
  workOrderReferences: string[];
  rollNumbers: string[];
  customerReferences: string[];
  orderTypes: OrderType[];
  /**
   * Only rolls that completed their production flow in the period: they have a
   * Knitting cost entry and entered the finished-goods warehouse that month.
   * Sent as the `onlyCompletedRolls` query param, not in the search command
   * body (the C# command lives in the Sierra.Finance.Models package).
   */
  onlyCompletedRolls: boolean;
}

const EMPTY_FILTERS: CostAnalysisFilters = {
  fabricCodes: [],
  colorReferences: [],
  productionCardNumbers: [],
  workOrderReferences: [],
  rollNumbers: [],
  customerReferences: [],
  orderTypes: [],
  onlyCompletedRolls: false,
};

@Injectable({ providedIn: 'root' })
export class CostAnalysisStateService {
  private readonly now = new Date();

  readonly selectedYear = signal<number>(this.now.getFullYear());
  readonly selectedMonth = signal<number>(this.now.getMonth() + 1);

  readonly excludedCounts = signal<{ greige: number; yarn: number; unknown: number }>({ greige: 0, yarn: 0, unknown: 0 });

  readonly costSourceFilter = signal<CostSourceFilter>('Estimated');
  readonly filters = signal<CostAnalysisFilters>(EMPTY_FILTERS);

  readonly activeTab = signal<CostAnalysisTabKey>('by-fabric');
  readonly scrollY = signal<number>(0);

  readonly rolls = signal<RollMonthlyCostDto[]>([]);
  readonly colorMap = signal<ReadonlyMap<string, ColorRecord>>(new Map());
  readonly workOrderMap = signal<ReadonlyMap<string, WorkOrderSummary>>(new Map());

  readonly byProcessReport = signal<ProcessSummaryReportDto | null>(null);

  /**
   * Bumped only when the page's Apply/Reset actually runs. By-process and
   * by-fabric gate their own reload on this (plus `filters()`) instead of on
   * `selectedYear`/`selectedMonth`/`costSourceFilter` directly, so picking a
   * year/month/cost source alone — without pressing Apply — never fires a
   * search.
   */
  readonly reloadRequested = signal(0);

  setPeriod(year: number, month: number): void {
    this.selectedYear.set(year);
    this.selectedMonth.set(month);
  }

  requestReload(): void {
    this.reloadRequested.update((v) => v + 1);
  }
}
